import { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { Staff, staffEmpty } from '../../../2_domain/staff/staff';
import { OneLineBoxContainer } from '../../core/components/box/boxContainer';
import Column from '../../core/components/box/column';
import Row from '../../core/components/box/row';
import SizedBox from '../../core/components/box/sizedBox';
import LXLargeButton from '../../core/components/buttons/lxLargeButton';
import LXMiddleButton from '../../core/components/buttons/lxMiddleButton';
import { LXLabelSelector } from '../../core/components/buttons/lxSelector';
import { LXLabelInput } from '../../core/texts/lxInput';
import { LXh2, LXh3, LXbody } from '../../core/texts/lxText';

interface StaffEditDialogProps {
  staff?: Staff;
  onCancel: () => Promise<void>;
  onSave: (newStaff: Staff) => Promise<void>;
}

export const StaffEditDialog = (props: StaffEditDialogProps) => {
  const theme = useTheme();
  const [staff, setStaff] = useState<Staff>(props.staff ?? staffEmpty);

  useEffect(() => {
    setStaff(props.staff ?? staffEmpty);
  }, [props.staff]);

  return (
    <Column>
      <LXh2>{theme.staff.staffInformation}</LXh2>
      <SizedBox height={40}></SizedBox>
      <Row>
        <LXLabelInput
          width={318}
          label={theme.texts.name}
          value={staff.name}
          onChange={(newName) => {
            setStaff((prev) => {
              return { ...prev, name: newName };
            });
          }}
          placeholder={theme.texts.enterName}
        ></LXLabelInput>
        <SizedBox width={24}></SizedBox>
        {/** FIX: this */}
        <LXLabelSelector<StaffKind>
          label={theme.staff.staffType}
          value={staff.kind}
          values={['Doctor', 'Nurse', 'Nonmedical Personnel']}
          labels={[theme.staff.doctor, theme.staff.nurse, theme.staff.manager]}
          onSelect={(newKind) => {
            setStaff((prev) => {
              return { ...prev, kind: newKind };
            });
          }}
          dropdownItemWidth={318}
          dropdownHeaderWidth={318}
        ></LXLabelSelector>
      </Row>
      <SizedBox height={24}></SizedBox>
      <LXLabelInput
        width={318}
        label={theme.texts.phone}
        value={staff.phone}
        onChange={(newPhone) => {
          setStaff((prev) => {
            return { ...prev, phone: newPhone };
          });
        }}
        placeholder={theme.texts.enterPhoneNumber}
      ></LXLabelInput>
      <SizedBox height={30}></SizedBox>
      <Row justifyContent="center">
        <LXLargeButton kind={'secondary'} onClick={props.onCancel}>
          {theme.texts.cancel}
        </LXLargeButton>
        <SizedBox width={8}></SizedBox>
        <LXLargeButton onClick={() => props.onSave(staff)}>
          {theme.texts.save}
        </LXLargeButton>
      </Row>
    </Column>
  );
};

interface StaffDeleteDialogProps {
  staff: Staff;
  onCancel: () => Promise<void>;
  onDelete: () => Promise<void>;
}

export const StaffDeleteDialog = (props: StaffDeleteDialogProps) => {
  const theme = useTheme();

  return (
    <Column justifyContent="space-between" alignItems="center">
      <SizedBox height={30}></SizedBox>
      <LXh3>{theme.staff.deleteStaff}</LXh3>
      <SizedBox height={8}></SizedBox>
      <LXbody color={theme.colors.caption}>
        {theme.staff.areYouSureYouWantToDeleteThisStaff}
      </LXbody>
      <LXbody color={theme.colors.caption}>
        {theme.texts.itWillBePermanentlyDeleted}
      </LXbody>
      <SizedBox height={24}></SizedBox>
      <OneLineBoxContainer>
        <LXbody>{`#${props.staff.serial} / ${props.staff.name}`}</LXbody>
      </OneLineBoxContainer>
      <SizedBox height={24}></SizedBox>
      <Row justifyContent="center">
        <LXMiddleButton onClick={props.onCancel} kind={'secondary'}>
          {theme.texts.cancel}
        </LXMiddleButton>
        <SizedBox width={8}></SizedBox>
        <LXMiddleButton onClick={props.onDelete}>
          {theme.staff.deleteStaff}
        </LXMiddleButton>
      </Row>
    </Column>
  );
};
